import { memo, useCallback } from 'react'
import { View, Text } from 'react-native'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { FlashList } from '@shopify/flash-list'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { SlidersHorizontal } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { PageShell } from '@/components/layout/PageShell'
import { VehicleCard } from '@/components/explore/VehicleCard'
import { VehicleListItem } from '@/components/explore/VehicleListItem'
import { useVehicles } from '@/lib/hooks/use-vehicles'
import { useTheme } from '@/lib/hooks/use-theme'
import { useExploreStore } from '@/lib/stores/explore.store'
import { useSemanticColors, INTERACTION } from '@/lib/theme/tokens'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import type { SearchVehicle } from '@/lib/types/vehicle.types'

/**
 * "Ver todos" reuses the explore store, so this catalog silently inherits the
 * filters the user last set on /explore. With no filter UI on this screen a
 * short list read as "there is no inventory" — this bar names the filters and
 * gives the one control that undoes them.
 */
function ActiveFiltersBar({ count, onClear }: { count: number; onClear: () => void }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View
      testID="all-vehicles.active-filters"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        paddingLeft: SPACING.md,
        paddingRight: SPACING.xs,
        paddingVertical: SPACING.xs,
        borderRadius: RADIUS.lg,
        backgroundColor: colors.surfaceSubtle,
      }}
    >
      <SlidersHorizontal size={16} color={COLORS.purple.DEFAULT} strokeWidth={2} />
      <Text
        style={{
          flex: 1,
          fontSize: 13,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
        }}
        numberOfLines={2}
      >
        {t('explore.filters_active_notice', { count })}
      </Text>
      <Pressable
        onPress={onClear}
        accessibilityRole="button"
        accessibilityLabel={t('explore.filter_reset')}
        testID="all-vehicles.clear-filters"
        style={{
          minWidth: INTERACTION.minTouchTarget,
          minHeight: INTERACTION.minTouchTarget,
          paddingHorizontal: SPACING.sm,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text
          style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textBrand }}
          numberOfLines={1}
        >
          {t('explore.filter_reset')}
        </Text>
      </Pressable>
    </View>
  )
}

const AllVehiclesCardTile = memo(function AllVehiclesCardTile({
  item,
  index,
  isFavorite,
  onVehiclePress,
  onToggleFavorite,
}: {
  item: SearchVehicle
  index: number
  isFavorite: boolean
  onVehiclePress: (vehicle: SearchVehicle) => void
  onToggleFavorite: (vehicleId: string) => void
}) {
  const handlePress = useCallback(() => onVehiclePress(item), [item, onVehiclePress])
  const handleFavorite = useCallback(() => onToggleFavorite(item.id), [item.id, onToggleFavorite])

  return (
    <View
      testID={`all-vehicles.card-${index}`}
      style={{ flexBasis: '31%', flexGrow: 1, minWidth: 280, maxWidth: 360 }}
    >
      <VehicleCard
        vehicle={item}
        isFavorite={isFavorite}
        onPress={handlePress}
        onToggleFavorite={handleFavorite}
        desktop
      />
    </View>
  )
})

const AllVehiclesListRow = memo(function AllVehiclesListRow({
  item,
  onVehiclePress,
}: {
  item: SearchVehicle
  onVehiclePress: (vehicle: SearchVehicle) => void
}) {
  const handlePress = useCallback(() => onVehiclePress(item), [item, onVehiclePress])
  return <VehicleListItem vehicle={item} onPress={handlePress} />
})

export default function AllVehiclesScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const { isDesktop } = useBreakpoint()
  const {
    vehicles,
    isLoading,
    isRefetching,
    error,
    refetch,
    favoriteIds,
    toggleFavorite,
    activeFilterCount,
  } = useVehicles({ scope: 'all' })

  const category = useExploreStore((s) => s.category)
  const pickupDate = useExploreStore((s) => s.pickupDate)
  const returnDate = useExploreStore((s) => s.returnDate)
  const setCategory = useExploreStore((s) => s.setCategory)
  const setDates = useExploreStore((s) => s.setDates)
  const resetFilters = useExploreStore((s) => s.resetFilters)

  // `activeFilterCount` only covers the filter sheet. The category chip and the
  // date range narrow this list too, and on this screen neither is visible.
  const totalActiveFilters =
    activeFilterCount + (category ? 1 : 0) + (pickupDate || returnDate ? 1 : 0)

  const handleClearFilters = useCallback(() => {
    setCategory(null)
    setDates(null, null)
    resetFilters()
  }, [resetFilters, setCategory, setDates])

  const handleVehiclePress = useCallback(
    (v: SearchVehicle) => router.push(`/explore/${v.id}` as never),
    [router],
  )
  const handleToggleFavorite = useCallback(
    (vehicleId: string) => toggleFavorite(vehicleId),
    [toggleFavorite],
  )
  const handleRefresh = useCallback(() => {
    void refetch()
  }, [refetch])

  // A failed request is not an empty catalog. Both used to render "No hay
  // vehículos cerca" with nothing to tap.
  const showError = Boolean(error) && vehicles.length === 0
  const clearFiltersAction =
    totalActiveFilters > 0
      ? { label: t('explore.filter_reset'), onPress: handleClearFilters }
      : undefined
  const showFiltersBar = totalActiveFilters > 0 && !isLoading && !showError

  if (isDesktop) {
    return (
      <ScreenContainer variant="dashboard" scroll={false}>
        <PageShell title={t('explore.all_vehicles_title')} back>
          <ScrollView
            showsVerticalScrollIndicator={false}
            refreshControl={
              <BrandedRefreshControl refreshing={isRefetching} onRefresh={handleRefresh} />
            }
            contentContainerStyle={{ paddingBottom: insets.bottom + SPACING.xl }}
          >
            {showFiltersBar ? (
              <View style={{ marginBottom: SPACING.md }}>
                <ActiveFiltersBar count={totalActiveFilters} onClear={handleClearFilters} />
              </View>
            ) : null}

            {isLoading ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.lg }}>
                {[0, 1, 2, 3, 4, 5].map((item) => (
                  <View key={item} style={{ flexBasis: '31%', flexGrow: 1, minWidth: 280 }}>
                    <VehicleCard.Skeleton desktop />
                  </View>
                ))}
              </View>
            ) : showError ? (
              <EmptyState
                variant="aurora"
                title={t('common.error_title')}
                description={t('common.error_description')}
                action={{ label: t('common.retry'), onPress: handleRefresh }}
              />
            ) : vehicles.length === 0 ? (
              <EmptyState
                title={t('explore.all_vehicles_empty')}
                compact
                variant="bloom"
                action={clearFiltersAction}
              />
            ) : (
              <View
                testID="all-vehicles.grid"
                style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.lg }}
              >
                {(vehicles as SearchVehicle[]).map((item, index) => (
                  <AllVehiclesCardTile
                    key={item.id}
                    item={item}
                    index={index}
                    isFavorite={favoriteIds.has(item.id)}
                    onVehiclePress={handleVehiclePress}
                    onToggleFavorite={handleToggleFavorite}
                  />
                ))}
              </View>
            )}
          </ScrollView>
        </PageShell>
      </ScreenContainer>
    )
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }} testID="all-vehicles.screen">
      <ScreenHeader
        title={t('explore.all_vehicles_title')}
        backTestID="all-vehicles.back-button"
        titleNumberOfLines={1}
        style={{ paddingTop: insets.top + SPACING.sm }}
      />

      {showFiltersBar ? (
        <View style={{ paddingHorizontal: SPACING.md, paddingBottom: SPACING.sm }}>
          <ActiveFiltersBar count={totalActiveFilters} onClear={handleClearFilters} />
        </View>
      ) : null}

      {/* List */}
      {isLoading ? (
        <View style={{ paddingHorizontal: SPACING.md, gap: SPACING.sm }}>
          <VehicleListItem.Skeleton />
          <VehicleListItem.Skeleton />
          <VehicleListItem.Skeleton />
          <VehicleListItem.Skeleton />
          <VehicleListItem.Skeleton />
        </View>
      ) : showError ? (
        <EmptyState
          variant="aurora"
          title={t('common.error_title')}
          description={t('common.error_description')}
          action={{ label: t('common.retry'), onPress: handleRefresh }}
        />
      ) : vehicles.length === 0 ? (
        <EmptyState title={t('explore.all_vehicles_empty')} compact action={clearFiltersAction} />
      ) : (
        <FlashList
          data={vehicles as SearchVehicle[]}
          keyExtractor={(item) => item.id}
          contentContainerStyle={{
            paddingHorizontal: SPACING.md,
            paddingBottom: insets.bottom + SPACING.lg,
          }}
          ItemSeparatorComponent={() => <View style={{ height: SPACING.sm }} />}
          renderItem={({ item }) => (
            <AllVehiclesListRow item={item} onVehiclePress={handleVehiclePress} />
          )}
          refreshControl={
            <BrandedRefreshControl refreshing={isRefetching} onRefresh={handleRefresh} />
          }
          testID="all-vehicles.list"
        />
      )}
    </View>
  )
}
